// Compact-view block: aggregates consecutive segments of the same speaker
// into one Paper card with a flat run of word spans across the contained
// sub-segments. Each word stays individually clickable (anchors the
// WordActionPopover from the parent view) so audio-jump and speaker-split
// keep working in compact mode.
//
// Inline-Edit is NOT offered here because a block can span multiple
// underlying segments and there's no obvious 1:1 mapping for the edit
// target. Users who want to edit text should switch back to the standard
// view via the toolbar toggle.

import React from 'react';
import { Box, Paper, Stack, Typography } from '@mui/material';

function formatTime(seconds) {
  if (typeof seconds !== 'number' || Number.isNaN(seconds)) return '—:—';
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function splitText(text) {
  return (text || '').trim().split(/\s+/).filter(Boolean);
}

function TranscriptionSpeakerBlock({
  block,
  speakerColor,
  activeWord,
  onWordAction,
}) {
  const { speaker, items = [], start, end } = block || {};
  const speakerLabel = speaker || 'SPEAKER_?';

  return (
    <Paper
      variant="outlined"
      sx={{
        px: 1.75,
        py: 1.25,
        borderRadius: 2,
        bgcolor: 'background.paper',
        transition: 'background-color 120ms ease',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: 0.5, minHeight: 24 }}
      >
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: speakerColor,
            flexShrink: 0,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: speakerColor,
            fontWeight: 600,
            letterSpacing: 0.3,
            textTransform: 'uppercase',
            fontSize: '0.7rem',
          }}
        >
          {speakerLabel}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            fontVariantNumeric: 'tabular-nums',
            flexShrink: 0,
          }}
        >
          {formatTime(start)} – {formatTime(end)}
        </Typography>
      </Stack>

      <Typography
        variant="body1"
        component="div"
        sx={{
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          lineHeight: 1.65,
          color: 'text.primary',
        }}
      >
        {items.map(({ segment, originalIndex }, subIdx) => {
          const words = segment.words;
          const hasWords = Array.isArray(words) && words.length > 0;
          if (!hasWords) {
            // Legacy segment without word-level timestamps: show plain text,
            // not clickable.
            const trailingSpace = subIdx < items.length - 1 ? ' ' : '';
            return (
              <span key={`plain-${originalIndex}`}>
                {(segment.text || '').trim()}
                {trailingSpace}
              </span>
            );
          }
          const wordText = words.map((w) => w.word).join(' ');
          const displayText = segment.text || wordText;
          const tokens = splitText(displayText);
          return tokens.map((token, idx) => {
            const word = words[idx];
            const canJump = typeof word?.start === 'number';
            const isActive =
              activeWord
              && activeWord.segmentIndex === originalIndex
              && activeWord.wordIndex === idx;
            const isLastTokenInBlock =
              subIdx === items.length - 1 && idx === tokens.length - 1;
            return (
              <span
                key={`${originalIndex}-${idx}`}
                title={
                  canJump
                    ? `${formatTime(word.start)} – ${formatTime(word.end)}`
                    : undefined
                }
                onClick={(event) => {
                  if (!canJump) return;
                  onWordAction?.({
                    segmentIndex: originalIndex,
                    wordIndex: idx,
                    word,
                    anchorEl: event.currentTarget,
                    totalWords: words.length,
                    wordStart: word.start,
                  });
                }}
                className={isActive ? 'tx-word tx-word-active' : 'tx-word'}
              >
                {token}
                {isLastTokenInBlock ? '' : ' '}
              </span>
            );
          });
        })}
      </Typography>
    </Paper>
  );
}

export default React.memo(TranscriptionSpeakerBlock);
